import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ErrorState from './../../ErrorState/ErrorState';
import PositionsListSkeleton from './PositionsListSkeleton';
import { useCachedFetch } from '../../../hooks/useCachedFetch';
import { AnimatedOnScroll } from '../../AnimatedOnScroll';
import './PositionsList.scss';

const PositionsList = () => {
    const { i18n } = useTranslation();
    const currentLang = i18n.language;

    const { data: positionsData, loading, error, refetch } = useCachedFetch('/data/locales/positions.json');

    const handleRetry = useCallback(() => {
        refetch();
    }, [refetch]);

    const [isLoading, setIsLoading] = useState(true);
    const [hasError, setHasError] = useState(false);

    const vacancies = useMemo(() => {
        return positionsData?.PositionsData?.vacancies || [];
    }, [positionsData]);

    const mainData = useMemo(() => {
        return positionsData?.PositionsData;
    }, [positionsData]);

    useEffect(() => {
        if (loading) return;

        if (error) {
            setHasError(true);
            setIsLoading(false);
            return;
        }

        if (vacancies.length > 0) {
            setHasError(false);
        } else {
            setHasError(false);
        }

        setIsLoading(false);
    }, [loading, error, vacancies.length]);

    const notAvailableMessage = useMemo(() => {
        return mainData?.notAvailable?.[currentLang] || mainData?.notAvailable?.en || "No available positions";
    }, [mainData, currentLang]);

    const getLocalizedTitle = useCallback((vacancy) => {
        return vacancy.positionTitle?.[currentLang] || vacancy.positionTitle?.en || '';
    }, [currentLang]);

    if (isLoading) {
        return <PositionsListSkeleton />;
    }

    if (hasError) {
        return <ErrorState onRetry={handleRetry} />;
    }

    if (vacancies.length === 0) {
        return (
            <div className="pos__list__section">
                <div className="pos__list__empty">
                    {notAvailableMessage}
                </div>
            </div>
        );
    }

    return (
        <div className="pos__list__section">
            {vacancies.map((vacancy) => (
                
                <div className="pos__list__item" key={vacancy.id}>
                    <AnimatedOnScroll animation="fade-up" delay={0}>
                    <Link 
                        to={`/careers/${vacancy.slug}`} 
                        className="pos__list__question"
                        aria-label={`View ${getLocalizedTitle(vacancy)} position`}
                    >
                        {getLocalizedTitle(vacancy)}
                        <span className="pos__list__arrow" aria-hidden="true"></span>
                    </Link>
                    </AnimatedOnScroll>
                </div>
            ))}
        </div>
    );
};

export default React.memo(PositionsList);